import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Sun, Sunset, Lock, Plus, Trash2, CalendarDays, Copy } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAppState } from "@/context/AppState";
import { dataApi } from "@/lib/agenda";

const TURNS = [
  { key: "morning", label: "Mañana", icon: Sun, tint: "#F4A62A", hours: [9, 10, 11, 12, 13] },
  { key: "afternoon", label: "Tarde", icon: Sunset, tint: "#275FBE", hours: [16, 17, 18, 19, 20] },
];

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

const formatBlockDate = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return `${d} de ${MESES[m - 1]} de ${y}`;
};

const formatBlockRange = (b) => {
  if (b.end_date && b.end_date !== b.date) {
    return `Del ${formatBlockDate(b.date)} al ${formatBlockDate(b.end_date)}`;
  }
  return formatBlockDate(b.date);
};

export default function Disponibilidad() {
  const { therapists } = useAppState();
  const [selected, setSelected] = useState("");
  const [week, setWeek] = useState([]);
  const [loading, setLoading] = useState(false);

  const [blocks, setBlocks] = useState([]);
  const [bMode, setBMode] = useState("single");
  const [bDate, setBDate] = useState("");
  const [bEndDate, setBEndDate] = useState("");
  const [bAllDay, setBAllDay] = useState(true);
  const [bFrom, setBFrom] = useState("14:00");
  const [bTo, setBTo] = useState("16:00");
  const [bNote, setBNote] = useState("");

  useEffect(() => {
    if (!selected && therapists.length) setSelected(therapists[0].id);
  }, [therapists, selected]);

  const load = useCallback(async (tid) => {
    if (!tid) return;
    setLoading(true);
    try {
      const [data, bl] = await Promise.all([
        dataApi.getAvailability(tid),
        dataApi.listBlocks(tid),
      ]);
      setWeek(data.week);
      setBlocks(bl);
    } catch (e) {
      toast.error("Algo no ha ido bien. Prueba otra vez o vuelve más tarde.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selected) load(selected);
  }, [selected, load]);

  const persist = async (newWeek) => {
    setWeek(newWeek);
    try {
      await dataApi.setAvailability(selected, newWeek);
    } catch (e) {
      toast.error("Algo no ha ido bien. Prueba otra vez o vuelve más tarde.");
    }
  };

  const toggleHour = (day, turnKey, hour) => {
    const newWeek = week.map((r) => {
      if (r.day !== day) return r;
      const set = new Set(r[turnKey] || []);
      if (set.has(hour)) set.delete(hour);
      else set.add(hour);
      return { ...r, [turnKey]: Array.from(set).sort((a, b) => a - b) };
    });
    persist(newWeek);
  };

  const toggleTurnAll = (day, turnKey, allHours) => {
    const newWeek = week.map((r) => {
      if (r.day !== day) return r;
      const full = (r[turnKey] || []).length === allHours.length;
      return { ...r, [turnKey]: full ? [] : [...allHours] };
    });
    persist(newWeek);
  };

  const copyDay = (day) => {
    const src = week.find((r) => r.day === day);
    if (!src) return;
    const newWeek = week.map((r) =>
      r.day === day ? r : { ...r, morning: [...(src.morning || [])], afternoon: [...(src.afternoon || [])] }
    );
    persist(newWeek);
    toast.success("Horario copiado a toda la semana.");
  };

  const addBlock = async () => {
    if (!bDate) {
      toast.error("Elige una fecha para el bloqueo.");
      return;
    }
    if (bMode === "range" && (!bEndDate || bEndDate < bDate)) {
      toast.error("Elige una fecha de fin posterior a la de inicio.");
      return;
    }
    try {
      const created = await dataApi.createBlock(selected, {
        date: bDate,
        end_date: bMode === "range" ? bEndDate : null,
        all_day: bAllDay,
        from_time: bAllDay ? null : bFrom,
        to_time: bAllDay ? null : bTo,
        note: bNote,
      });
      setBlocks((list) => [...list, created].sort((a, b) => a.date.localeCompare(b.date)));
      setBDate("");
      setBEndDate("");
      setBNote("");
      setBAllDay(true);
      setBMode("single");
      toast.success("Listo. Has marcado ese tiempo como ocupado.");
    } catch (e) {
      toast.error("Algo no ha ido bien. Prueba otra vez o vuelve más tarde.");
    }
  };

  const removeBlock = async (id) => {
    try {
      await dataApi.deleteBlock(id);
      setBlocks((list) => list.filter((b) => b.id !== id));
    } catch (e) {
      toast.error("Algo no ha ido bien. Prueba otra vez o vuelve más tarde.");
    }
  };

  return (
    <div className="space-y-8 animate-fade-up" data-testid="disponibilidad-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Disponibilidad</h1>
        <p className="text-sm text-slate-500 mt-1">Tu planning semanal con los turnos de mañana y tarde.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {therapists.map((t) => (
          <button
            key={t.id}
            onClick={() => setSelected(t.id)}
            data-testid={`availability-therapist-${t.id}`}
            className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              selected === t.id ? "text-white border-transparent" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
            }`}
            style={selected === t.id ? { backgroundColor: t.color } : {}}
          >
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: selected === t.id ? "#fff" : t.color }} />
            {t.name}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-slate-400">Cargando planning</p>
      ) : (
        <>
          <div className="overflow-hidden rounded-3xl border border-[#EAE5DF] bg-white shadow-[0_18px_50px_-28px_rgba(46,42,143,0.22)]" data-testid="availability-grid">
            <div className="hidden sm:grid grid-cols-[150px_1fr_1fr] bg-[#FAF7F2] border-b border-[#EAE5DF]">
              <div className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Día</div>
              {TURNS.map((turn) => (
                <div key={turn.key} className="px-5 py-3 flex items-center gap-2">
                  <turn.icon className="h-4 w-4" style={{ color: turn.tint }} />
                  <span className="font-heading text-sm font-semibold text-slate-700">{turn.label}</span>
                </div>
              ))}
            </div>

            {week.map((row, idx) => (
              <div
                key={row.day}
                className={`grid grid-cols-1 sm:grid-cols-[150px_1fr_1fr] ${idx !== week.length - 1 ? "border-b border-[#F0EBE4]" : ""}`}
              >
                <div className="px-5 py-4 flex flex-col justify-center gap-1.5 bg-[#FDFBF8] sm:bg-transparent">
                  <span className="font-heading font-semibold text-slate-800">{row.day}</span>
                  <button
                    onClick={() => copyDay(row.day)}
                    data-testid={`copy-day-${row.day}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-slate-400 hover:text-[#275FBE] transition-colors self-start"
                  >
                    <Copy className="h-3 w-3" />
                    Copiar a la semana
                  </button>
                </div>
                {TURNS.map((turn) => {
                  const enabled = row[turn.key] || [];
                  const allOn = enabled.length === turn.hours.length;
                  return (
                    <div key={turn.key} className="px-5 py-4 border-t border-[#F0EBE4] sm:border-t-0 sm:border-l sm:border-[#F0EBE4]">
                      <div className="flex items-center justify-between mb-2 sm:hidden">
                        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600">
                          <turn.icon className="h-4 w-4" style={{ color: turn.tint }} />
                          {turn.label}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {turn.hours.map((h) => {
                          const on = enabled.includes(h);
                          return (
                            <button
                              key={h}
                              onClick={() => toggleHour(row.day, turn.key, h)}
                              data-testid={`hour-${turn.key}-${row.day}-${h}`}
                              aria-pressed={on}
                              className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-all duration-200 ${
                                on ? "text-white border-transparent shadow-sm" : "bg-white border-slate-200 text-slate-400 hover:border-slate-300"
                              }`}
                              style={on ? { backgroundColor: turn.tint } : {}}
                            >
                              {h}-{h + 1}h
                            </button>
                          );
                        })}
                        <button
                          onClick={() => toggleTurnAll(row.day, turn.key, turn.hours)}
                          data-testid={`toggle-all-${turn.key}-${row.day}`}
                          className="ml-1 text-xs font-medium text-slate-400 hover:text-slate-600 transition-colors"
                        >
                          {allOn ? "Quitar todo" : "Todo"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Marcar como ocupado */}
          <div className="rounded-3xl border border-[#EAE5DF] bg-white shadow-[0_18px_50px_-28px_rgba(46,42,143,0.22)] overflow-hidden" data-testid="blocks-section">
            <div className="px-6 py-4 bg-[#FAF7F2] border-b border-[#EAE5DF] flex items-center gap-2">
              <Lock className="h-4 w-4" style={{ color: "#2E2A8F" }} />
              <div>
                <h2 className="font-heading text-lg font-semibold text-slate-800">Marcar como ocupado</h2>
                <p className="text-xs text-slate-500">Reserva un día entero o un tramo de horas en los que no atiendes.</p>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <div className="flex flex-wrap items-end gap-4">
                <div className="flex rounded-full bg-slate-100 p-1">
                  <button
                    onClick={() => setBMode("single")}
                    data-testid="block-mode-single"
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${bMode === "single" ? "bg-white shadow text-slate-800" : "text-slate-500"}`}
                  >
                    Un día
                  </button>
                  <button
                    onClick={() => setBMode("range")}
                    data-testid="block-mode-range"
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${bMode === "range" ? "bg-white shadow text-slate-800" : "text-slate-500"}`}
                  >
                    Varios días
                  </button>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="block-date">{bMode === "range" ? "Desde" : "Fecha"}</Label>
                  <Input
                    id="block-date"
                    type="date"
                    value={bDate}
                    onChange={(e) => setBDate(e.target.value)}
                    data-testid="block-date"
                    className="h-10 w-[180px] rounded-xl border-slate-200 text-sm"
                  />
                </div>

                {bMode === "range" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="block-end-date">Hasta</Label>
                    <Input
                      id="block-end-date"
                      type="date"
                      value={bEndDate}
                      min={bDate || undefined}
                      onChange={(e) => setBEndDate(e.target.value)}
                      data-testid="block-end-date"
                      className="h-10 w-[180px] rounded-xl border-slate-200 text-sm"
                    />
                  </div>
                )}

                <div className="flex rounded-full bg-slate-100 p-1">
                  <button
                    onClick={() => setBAllDay(true)}
                    data-testid="block-allday"
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${bAllDay ? "bg-white shadow text-slate-800" : "text-slate-500"}`}
                  >
                    Todo el día
                  </button>
                  <button
                    onClick={() => setBAllDay(false)}
                    data-testid="block-tramo"
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${!bAllDay ? "bg-white shadow text-slate-800" : "text-slate-500"}`}
                  >
                    Un tramo
                  </button>
                </div>

                {!bAllDay && (
                  <div className="flex items-center gap-2">
                    <Input
                      type="time"
                      step={1800}
                      value={bFrom}
                      onChange={(e) => setBFrom(e.target.value)}
                      data-testid="block-from"
                      className="h-10 w-[110px] rounded-xl border-slate-200 text-sm"
                    />
                    <span className="text-slate-400 text-sm">a</span>
                    <Input
                      type="time"
                      step={1800}
                      value={bTo}
                      onChange={(e) => setBTo(e.target.value)}
                      data-testid="block-to"
                      className="h-10 w-[110px] rounded-xl border-slate-200 text-sm"
                    />
                  </div>
                )}

                <div className="space-y-1.5 flex-1 min-w-[160px]">
                  <Label htmlFor="block-note">Nota (opcional)</Label>
                  <Input
                    id="block-note"
                    value={bNote}
                    onChange={(e) => setBNote(e.target.value)}
                    placeholder="Motivo breve"
                    data-testid="block-note"
                    className="h-10 rounded-xl border-slate-200 text-sm"
                  />
                </div>

                <Button
                  onClick={addBlock}
                  data-testid="add-block-button"
                  className="h-10 rounded-full text-white gap-1.5"
                  style={{ backgroundColor: "#275FBE" }}
                >
                  <Plus className="h-4 w-4" />
                  Añadir bloqueo
                </Button>
              </div>

              <div className="space-y-2" data-testid="blocks-list">
                {blocks.length === 0 ? (
                  <p className="text-sm text-slate-400">Todavía no has marcado ningún tiempo como ocupado.</p>
                ) : (
                  blocks.map((b) => (
                    <div
                      key={b.id}
                      data-testid={`block-${b.id}`}
                      className="flex items-center justify-between gap-4 rounded-2xl border border-[#F0EBE4] bg-[#FDFBF8] px-4 py-3"
                    >
                      <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#2E2A8F]/10">
                          <CalendarDays className="h-4 w-4" style={{ color: "#2E2A8F" }} />
                        </span>
                        <div>
                          <p className="font-medium text-slate-800 text-sm">{formatBlockRange(b)}</p>
                          <p className="text-xs text-slate-500">
                            {b.all_day ? "Todo el día · Ocupado" : `${b.from_time} a ${b.to_time} · Ocupado`}
                            {b.note ? ` · ${b.note}` : ""}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => removeBlock(b.id)}
                        data-testid={`remove-block-${b.id}`}
                        className="text-slate-400 hover:text-red-600 transition-colors"
                        aria-label="Quitar bloqueo"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
